import type { ReactElement } from 'react';
import type { MCPGatewaySyncState } from '@kirin/api-client/contracts/mcp';
import { cn } from '@/lib/utils';
import { isPolicyApplied } from './gatewayControlUtils';

const STALE_WARNING_MINUTES = 15;

function minutesSince(iso: string): number {
  // A client clock behind the server's would otherwise produce a negative
  // value; clamp to 0 so we never render "-N min ago".
  return Math.max(
    0,
    Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  );
}

interface GatewaySyncFooterProps {
  gatewaySync: MCPGatewaySyncState;
}

/**
 * Two independent truthful states (KIR-3940 "Architecture constraint"): when
 * the gateway last made contact, and whether the current policy has been
 * applied. Last-known data stays visible in every state — nothing is hidden
 * while stale/never-synced, only flagged.
 */
export function GatewaySyncFooter({
  gatewaySync,
}: GatewaySyncFooterProps): ReactElement {
  const { lastContactAt, policyVersion, appliedPolicyVersion } = gatewaySync;

  const contactLabel =
    lastContactAt === null ? (
      <span className="text-muted-foreground">
        Waiting for gateway connection
      </span>
    ) : (
      (() => {
        const minutes = minutesSince(lastContactAt);
        const isStale = minutes >= STALE_WARNING_MINUTES;
        return (
          <span
            className={cn(
              isStale ? 'text-orange-600' : 'text-muted-foreground'
            )}
          >
            Last synced from gateway: {minutes} min ago
          </span>
        );
      })()
    );

  const applied = isPolicyApplied(policyVersion, appliedPolicyVersion);
  const policyLabel =
    policyVersion === null
      ? null
      : applied && lastContactAt !== null
        ? `Policy synced to gateway: ${minutesSince(lastContactAt)} min ago`
        : 'Policy synced to gateway: Pending';

  return (
    <div className="flex items-center gap-4 px-4 py-2 text-xs shrink-0">
      {contactLabel}
      {policyLabel && (
        <span className="text-muted-foreground">{policyLabel}</span>
      )}
    </div>
  );
}
